import { useEffect, useMemo, useState } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Button,
  Switch,
  DatePicker,
  Card,
  Space,
  Divider,
  Tooltip,
  Tag,
  Select
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  TagOutlined,
  TeamOutlined,
  CalendarOutlined,
  DollarOutlined,
  InfoCircleOutlined,
  PercentageOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import {
  internalCustomPlans,
  internalGstRates
} from '../../api/internalAdminEndpoints';
import { useAppNotifications } from '../../utils/notify';
import { digitsOnly, gstinInput } from '../../utils/validators';
import LoadingBlock from '../../components/LoadingBlock';
import PageHeader from '../../components/PageHeader';
import { paiseToINR } from '../../components/internalAdmin/AccountCells';

// Stage 5N-4: Custom Plan create/edit form.
//
// Pricing model now matches the operator's mental model:
//   1. Operator enters BASE amount (before discount, before GST)
//   2. Operator picks discount type: none / percent / fixed
//   3. Backend computes taxable, GST, total payable
//
// Free months are additive — the customer pays for `durationDays` and
// gets `freeMonths` × 30 days of access on top.
//
// Backend remains the source of truth for the GST/discount math; the
// live preview here is a hint only.

function rupeesToPaise(rupees) {
  if (rupees === null || rupees === undefined || rupees === '') return null;
  const n = Number(rupees);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}
function paiseToRupees(paise) {
  if (paise === null || paise === undefined) return null;
  const n = Number(paise);
  if (!Number.isFinite(n)) return null;
  return Math.round(n) / 100;
}

function calcPreview({ baseRupees, discountType, discountPercent, discountRupees, gstRatePercent }) {
  const basePaise = rupeesToPaise(baseRupees) || 0;
  let discountPaise = 0;
  if (discountType === 'percent' && Number(discountPercent) > 0) {
    discountPaise = Math.round((basePaise * Number(discountPercent)) / 100);
  } else if (discountType === 'fixed' && Number(discountRupees) > 0) {
    discountPaise = Math.min(basePaise, rupeesToPaise(discountRupees) || 0);
  }
  const taxablePaise = Math.max(0, basePaise - discountPaise);
  const rate = Number(gstRatePercent) || 0;
  const gstPaise = Math.round((taxablePaise * rate) / 100);
  const totalPaise = taxablePaise + gstPaise;
  return { basePaise, discountPaise, taxablePaise, gstPaise, totalPaise, gstRatePercent: rate };
}

export default function CustomPlanFormPage({ mode = 'create' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notifySuccess, notifyError } = useAppNotifications();
  const [form] = Form.useForm();

  const isEdit = mode === 'edit';

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [original, setOriginal] = useState(null);
  const [rates, setRates] = useState([]);
  const [livePreview, setLivePreview] = useState({
    basePaise: 0, discountPaise: 0, taxablePaise: 0, gstPaise: 0,
    totalPaise: 0, gstRatePercent: 18,
    includedUsers: 0, maxUsers: 0, durationDays: 0, freeMonths: 0
  });

  const discountType = Form.useWatch('discountType', form);

  // ---- Load rates + (in edit mode) the existing plan -----------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await internalGstRates.list();
        if (cancelled) return;
        const items = (r.items || []).filter((x) => x.isActive);
        setRates(items);

        if (!isEdit) {
          const def = items.find((x) => x.isDefault) || items[0];
          form.setFieldsValue({
            gstRateId: def ? def.id : undefined,
            discountType: 'none',
            publicSignupAllowed: true,
            maxRedemptionPerGstin: 1,
            durationDays: 30,
            freeMonths: 0,
            includedUsers: 10,
            maxUsers: 10
          });
          setLivePreview((p) => ({
            ...p,
            gstRatePercent: def ? Number(def.ratePercent) : 18
          }));
          setLoading(false);
          return;
        }

        const data = await internalCustomPlans.get(id);
        if (cancelled) return;
        setOriginal(data);
        form.setFieldsValue({
          customCode:            data.customCode,
          planName:              data.planName,
          description:           data.description || '',
          internalNotes:         data.internalNotes || '',
          includedUsers:         data.includedUsers,
          maxUsers:              data.maxUsers || data.includedUsers,
          durationDays:          data.durationDays,
          freeMonths:            data.freeMonths || 0,
          baseRupees:            paiseToRupees(data.basePricePaise),
          discountType:          data.discountType || 'none',
          discountPercent:       data.discountPercent || null,
          discountRupees:        data.discountType === 'fixed'
                                   ? paiseToRupees(data.discountPaise)
                                   : null,
          gstRateId:             data.gstRateId
                                   || (items.find((x) => Number(x.ratePercent) === Number(data.gstRatePercent)) || {}).id,
          validFrom:             data.validFrom ? dayjs(data.validFrom) : null,
          validUntil:            data.validUntil ? dayjs(data.validUntil) : null,
          maxRedemptions:        data.maxRedemptions,
          maxRedemptionPerGstin: data.maxRedemptionPerGstin,
          restrictedGstin:       data.restrictedGstin || '',
          restrictedEmail:       data.restrictedEmail || '',
          restrictedMobile:      data.restrictedMobile || '',
          publicSignupAllowed:   data.publicSignupAllowed !== false
        });
        recalcFromForm(form.getFieldsValue());
      } catch (err) {
        if (!cancelled) {
          notifyError(err, { title: 'Could not load Custom Plan' });
          navigate('/internal/custom-plans');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  function recalcFromForm(all) {
    const rate = (rates.find((r) => r.id === all.gstRateId) || {}).ratePercent;
    const p = calcPreview({
      baseRupees: all.baseRupees,
      discountType: all.discountType,
      discountPercent: all.discountPercent,
      discountRupees: all.discountRupees,
      gstRatePercent: rate
    });
    setLivePreview({
      ...p,
      includedUsers: Number(all.includedUsers || 0),
      maxUsers: Number(all.maxUsers || 0),
      durationDays: Number(all.durationDays || 0),
      freeMonths: Number(all.freeMonths || 0)
    });
  }

  function onValuesChange(_changed, all) {
    recalcFromForm(all);
    if (typeof all.customCode === 'string') {
      const upper = all.customCode.toUpperCase();
      if (upper !== all.customCode) {
        form.setFieldsValue({ customCode: upper });
      }
    }
    // When max_users < included_users, lift it to keep the form
    // self-consistent with backend validation.
    if (Number(all.includedUsers) > Number(all.maxUsers || 0)) {
      form.setFieldsValue({ maxUsers: Number(all.includedUsers) });
    }
  }

  async function handleSubmit(values) {
    const payload = {
      planName:               values.planName,
      description:            values.description || null,
      internalNotes:          values.internalNotes || null,
      includedUsers:          Number(values.includedUsers),
      maxUsers:               Number(values.maxUsers || values.includedUsers),
      durationDays:           Number(values.durationDays),
      freeMonths:             Number(values.freeMonths || 0),
      basePricePaise:         rupeesToPaise(values.baseRupees),
      discountType:           values.discountType || 'none',
      gstRateId:              values.gstRateId,
      validFrom:              values.validFrom ? values.validFrom.toISOString() : null,
      validUntil:             values.validUntil ? values.validUntil.toISOString() : null,
      maxRedemptions:         values.maxRedemptions ?? null,
      maxRedemptionPerGstin:  values.maxRedemptionPerGstin ?? 1,
      restrictedGstin:        values.restrictedGstin
                                ? String(values.restrictedGstin).trim().toUpperCase()
                                : null,
      restrictedEmail:        values.restrictedEmail
                                ? String(values.restrictedEmail).trim().toLowerCase()
                                : null,
      restrictedMobile:       values.restrictedMobile
                                ? String(values.restrictedMobile).trim()
                                : null,
      publicSignupAllowed:    values.publicSignupAllowed !== false
    };
    if (payload.discountType === 'percent') {
      payload.discountPercent = Number(values.discountPercent);
    } else if (payload.discountType === 'fixed') {
      payload.discountPaise = rupeesToPaise(values.discountRupees);
    }

    setSubmitting(true);
    try {
      let saved;
      if (isEdit) {
        saved = await internalCustomPlans.update(id, payload);
        notifySuccess('Custom Plan updated', `${saved.customCode} has been updated.`);
      } else {
        payload.customCode = String(values.customCode || '').trim().toUpperCase();
        saved = await internalCustomPlans.create(payload);
        notifySuccess('Custom Plan created', `${saved.customCode} is now live.`);
      }
      navigate(`/internal/custom-plans/${saved.id}`);
    } catch (err) {
      notifyError(err, {
        title: isEdit ? 'Could not update Custom Plan' : 'Could not create Custom Plan'
      });
    } finally {
      setSubmitting(false);
    }
  }

  const codeLocked = isEdit;

  if (loading) return <LoadingBlock tip="Loading Custom Plan…" />;

  return (
    <div>
      <PageHeader
        title={isEdit ? 'Edit Custom Plan' : 'Create Custom Plan'}
        subtitle={
          isEdit
            ? `Update pricing, validity, and restrictions for ${original ? original.customCode : ''}.`
            : 'Define a new enterprise Custom Code customers can redeem during signup.'
        }
        actions={
          <Space>
            <Button icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/internal/custom-plans')} disabled={submitting}>
              Back
            </Button>
          </Space>
        }
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={onValuesChange}
        scrollToFirstError
      >
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-4">
            {/* ---- Basics ---- */}
            <Card size="small"
              title={<span className="flex items-center gap-2"><TagOutlined className="text-brand-500" />Plan basics</span>}>
              <div className="grid gap-3 sm:grid-cols-2">
                <Form.Item label="Custom Code" name="customCode"
                  rules={[
                    { required: true, message: 'Custom Code is required' },
                    { pattern: /^[A-Z0-9_-]{4,40}$/, message: '4–40 chars, A–Z 0–9 dash underscore' }
                  ]}
                  tooltip="Customers will type this code at signup. Uppercase. 4–40 characters.">
                  <Input placeholder="e.g. ENTERPRISE99" maxLength={40} disabled={codeLocked}
                    style={{ fontFamily: 'monospace', textTransform: 'uppercase' }} />
                </Form.Item>
                <Form.Item label="Plan Name" name="planName"
                  rules={[{ required: true, message: 'Plan name is required' }, { min: 3, max: 100 }]}>
                  <Input placeholder="e.g. Enterprise 30-day plan" maxLength={100} />
                </Form.Item>
              </div>
              <Form.Item label="Description" name="description">
                <Input.TextArea rows={2} maxLength={500}
                  placeholder="Short description for internal reference." />
              </Form.Item>
              <Form.Item label={
                <span>Internal notes{' '}
                  <Tooltip title="Operator-only. Never exposed to public signup.">
                    <InfoCircleOutlined className="text-ink-400" />
                  </Tooltip>
                </span>
              } name="internalNotes">
                <Input.TextArea rows={2} maxLength={1000}
                  placeholder="Reason for the plan, point of contact, etc." />
              </Form.Item>
            </Card>

            {/* ---- Users & duration ---- */}
            <Card size="small"
              title={<span className="flex items-center gap-2"><TeamOutlined className="text-brand-500" />Users &amp; duration</span>}>
              <div className="grid gap-3 sm:grid-cols-4">
                <Form.Item label="Included Users" name="includedUsers"
                  rules={[{ required: true, type: 'number', min: 1, max: 1000 }]}>
                  <InputNumber min={1} max={1000} className="!w-full" />
                </Form.Item>
                <Form.Item label="Max Users" name="maxUsers"
                  rules={[{ required: true, type: 'number', min: 1, max: 10000 }]}
                  tooltip="Tenant cannot add more than this many users.">
                  <InputNumber min={1} max={10000} className="!w-full" />
                </Form.Item>
                <Form.Item label="Duration (days)" name="durationDays"
                  rules={[{ required: true, type: 'number', min: 1, max: 3650 }]}>
                  <InputNumber min={1} max={3650} className="!w-full" />
                </Form.Item>
                <Form.Item label="Free months" name="freeMonths"
                  rules={[{ type: 'number', min: 0, max: 60 }]}
                  tooltip="Adds free access AFTER the paid duration. Does not change taxable amount.">
                  <InputNumber min={0} max={60} className="!w-full" />
                </Form.Item>
              </div>
            </Card>

            {/* ---- Pricing & GST ---- */}
            <Card size="small"
              title={<span className="flex items-center gap-2"><DollarOutlined className="text-brand-500" />Pricing &amp; GST</span>}>
              <div className="grid gap-3 sm:grid-cols-2">
                <Form.Item label="Base amount before discount (₹)" name="baseRupees"
                  rules={[{ required: true, type: 'number', min: 1, max: 1000000 }]}
                  tooltip="The price BEFORE any discount or GST. Backend computes the rest.">
                  <InputNumber min={1} max={1000000} step={1} precision={2} className="!w-full" prefix="₹" />
                </Form.Item>
                <Form.Item label="GST rate" name="gstRateId" rules={[{ required: true }]}>
                  <Select
                    options={(rates || []).map((r) => ({
                      value: r.id,
                      label: `${r.rateName} — ${Number(r.ratePercent).toFixed(2)}%${r.isDefault ? ' (default)' : ''}`
                    }))}
                  />
                </Form.Item>
              </div>

              <Divider style={{ margin: '8px 0 12px' }} />

              <Form.Item label="Discount" name="discountType" rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: 'none',    label: 'None' },
                    { value: 'percent', label: 'Percent off' },
                    { value: 'fixed',   label: 'Fixed INR off' }
                  ]}
                />
              </Form.Item>
              {discountType === 'percent' ? (
                <Form.Item label="Discount %" name="discountPercent"
                  rules={[{ required: true, type: 'number', min: 0.01, max: 100 }]}>
                  <InputNumber min={0.01} max={100} step={0.5} className="!w-full" addonAfter="%" />
                </Form.Item>
              ) : null}
              {discountType === 'fixed' ? (
                <Form.Item label="Discount (₹)" name="discountRupees"
                  rules={[{ required: true, type: 'number', min: 1 }]}>
                  <InputNumber min={1} step={1} precision={2} className="!w-full" prefix="₹" />
                </Form.Item>
              ) : null}
            </Card>

            {/* ---- Validity & redemption limits ---- */}
            <Card size="small"
              title={<span className="flex items-center gap-2"><CalendarOutlined className="text-brand-500" />Validity &amp; redemption limits</span>}>
              <div className="grid gap-3 sm:grid-cols-2">
                <Form.Item label="Valid from" name="validFrom">
                  <DatePicker showTime className="!w-full" placeholder="Open-ended start" />
                </Form.Item>
                <Form.Item label="Valid until" name="validUntil"
                  dependencies={['validFrom']}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_rule, value) {
                        const start = getFieldValue('validFrom');
                        if (start && value && !value.isAfter(start)) {
                          return Promise.reject(new Error('Valid until must be after Valid from'));
                        }
                        return Promise.resolve();
                      }
                    })
                  ]}>
                  <DatePicker showTime className="!w-full" placeholder="Open-ended end" />
                </Form.Item>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Form.Item label="Max Redemptions (global)" name="maxRedemptions"
                  tooltip="Leave empty for unlimited.">
                  <InputNumber min={1} max={1000000} className="!w-full" placeholder="Unlimited" />
                </Form.Item>
                <Form.Item label="Max Redemptions per GSTIN" name="maxRedemptionPerGstin"
                  rules={[{ required: true, type: 'number', min: 1, max: 1000 }]}>
                  <InputNumber min={1} max={1000} className="!w-full" />
                </Form.Item>
              </div>
            </Card>

            {/* ---- Restrictions ---- */}
            <Card size="small"
              title={<span className="flex items-center gap-2"><InfoCircleOutlined className="text-brand-500" />Customer restrictions</span>}>
              <p className="text-xs text-ink-500 mt-0 mb-3">
                Each restriction tightens who can redeem this Custom Code. Leave blank to allow any.
              </p>
              <div className="grid gap-3 sm:grid-cols-3">
                <Form.Item label="Restricted GSTIN" name="restrictedGstin"
                  getValueFromEvent={gstinInput}
                  rules={[{ pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}Z[0-9A-Z]{1}$/,
                            message: 'Not a valid GSTIN' }]}>
                  <Input
                    placeholder="e.g. 27ABCDE1234F1Z5"
                    maxLength={15}
                    autoCapitalize="characters"
                    spellCheck={false}
                  />
                </Form.Item>
                <Form.Item label="Restricted email" name="restrictedEmail"
                  rules={[{ type: 'email', message: 'Not a valid email',
                            transform: (v) => (v ? String(v).trim().toLowerCase() : v) }]}>
                  <Input placeholder="customer@example.com" maxLength={254} />
                </Form.Item>
                <Form.Item label="Restricted mobile" name="restrictedMobile"
                  getValueFromEvent={digitsOnly(10)}
                  rules={[{ pattern: /^[6-9][0-9]{9}$/, message: '10-digit Indian mobile' }]}>
                  <Input
                    placeholder="9876543210"
                    maxLength={10}
                    inputMode="numeric"
                    autoComplete="tel"
                  />
                </Form.Item>
              </div>
            </Card>

            <Card size="small" title="Availability">
              <div className="flex items-center justify-between">
                <div>
                  <p className="m-0 text-sm font-semibold text-ink-900">Public signup allowed</p>
                  <p className="m-0 text-xs text-ink-500">
                    Off = code exists but no one can redeem it via public signup.
                  </p>
                </div>
                <Form.Item name="publicSignupAllowed" valuePropName="checked" noStyle>
                  <Switch />
                </Form.Item>
              </div>
              <p className="text-[11px] text-ink-500 mt-3 mb-0">
                Payment is always required. Custom Plans never have a no-payment public path.
              </p>
            </Card>

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button onClick={() => navigate('/internal/custom-plans')} disabled={submitting}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={submitting}>
                {isEdit ? 'Save changes' : 'Create Custom Plan'}
              </Button>
            </div>
          </div>

          {/* ---- Live preview (right column) ---- */}
          <div className="lg:col-span-1">
            <Card size="small" className="sticky top-20"
              title={<span className="flex items-center gap-2"><PercentageOutlined className="text-brand-500" />Live preview</span>}>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-ink-500">Base amount</span>
                  <span>{paiseToINR(livePreview.basePaise)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ink-500">Discount</span>
                  <span className="text-rose-600">
                    {livePreview.discountPaise > 0 ? `− ${paiseToINR(livePreview.discountPaise)}` : '—'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ink-500">Taxable amount</span>
                  <span>{paiseToINR(livePreview.taxablePaise)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ink-500">GST {livePreview.gstRatePercent}%</span>
                  <span>{paiseToINR(livePreview.gstPaise)}</span>
                </div>
                <Divider style={{ margin: '6px 0' }} />
                <div className="flex justify-between text-base font-bold">
                  <span>Total payable</span>
                  <span>{paiseToINR(livePreview.totalPaise)}</span>
                </div>
                <Divider style={{ margin: '6px 0' }} />
                <div className="flex justify-between">
                  <span className="text-ink-500">Users (incl / max)</span>
                  <span>{livePreview.includedUsers || '—'} / {livePreview.maxUsers || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ink-500">Paid duration</span>
                  <span>{livePreview.durationDays ? `${livePreview.durationDays} days` : '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ink-500">Free months</span>
                  <span>{livePreview.freeMonths || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ink-500">Total access</span>
                  <span className="font-medium">
                    {livePreview.durationDays
                      ? `${livePreview.durationDays + livePreview.freeMonths * 30} days`
                      : '—'}
                  </span>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-surface-200">
                <Tag color="blue" bordered={false}>
                  Backend recalculates the GST split on save
                </Tag>
              </div>

              {isEdit && original && original.redemptionCount > 0 ? (
                <div className="mt-3 p-2 rounded bg-amber-50 text-amber-800 text-xs">
                  This plan has {original.redemptionCount} redemption
                  {original.redemptionCount === 1 ? '' : 's'}. Updates apply
                  to future redemptions only.
                </div>
              ) : null}
            </Card>
          </div>
        </div>
      </Form>
    </div>
  );
}
